import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMemo, useRef, useState } from 'react';

import { PillRadioGroup } from '~/components/common/PillRadioGroup';
import { useTasksEnabled } from '~/components/tasks/useTasksEnabled';
import type { KnowledgeEntry, KnowledgeEntryKindEnum } from '~/data/generated/model';
import { propertiesKnowledgeCreateBodyContentMax } from '~/data/generated/zod/properties/properties.zod';
import { usePropertyKnowledge, useSaveKnowledge } from '~/data/hooks/useKnowledge';
import {
  type KnowledgeTarget,
  knowledgeTargetLabel,
  useKnowledgeTargets,
} from '~/data/hooks/useKnowledgeTargets';
import { BUTTON_SX } from '~/theme';

import { formatKnowledgeDate, KNOWLEDGE_KIND_OPTIONS } from './KnowledgeBits';
import { findSupersedable } from './knowledgeMatch';

export const PROPERTY_WIDE_LABEL = 'Property-wide';

export interface AddKnowledgeDialogProps {
  initialTarget?: KnowledgeTarget;
  initialContent?: string;
  initialKind?: KnowledgeEntryKindEnum;
  /** The entry this one replaces, chosen up front from its row's Supersede… action. */
  initialReplaces?: KnowledgeEntry;
  /** The car tab records against its own elevator only. */
  lockCar?: boolean;
  onClose: () => void;
}

type CarOption = { id: string; name: string };

const PROPERTY_WIDE: CarOption = { id: '', name: PROPERTY_WIDE_LABEL };

const entrySummary = (entry: KnowledgeEntry, max = 80) => {
  const text = entry.content.length > max ? `${entry.content.slice(0, max)}…` : entry.content;
  return `${text} · ${formatKnowledgeDate(entry.createdAt)}`;
};

export function AddKnowledgeDialog({
  initialTarget,
  initialContent = '',
  initialKind,
  initialReplaces,
  lockCar = false,
  onClose,
}: AddKnowledgeDialogProps) {
  const { properties } = useKnowledgeTargets();
  const saveKnowledge = useSaveKnowledge();
  const tasksEnabled = useTasksEnabled();
  const { enqueueSnackbar } = useSnackbar();

  const [propertyId, setPropertyId] = useState<string | null>(initialTarget?.propertyId ?? null);
  const [carId, setCarId] = useState<string>(initialTarget?.carId ?? initialReplaces?.carId ?? '');
  const [kind, setKind] = useState<KnowledgeEntryKindEnum | null>(initialKind ?? null);
  const [content, setContent] = useState(initialContent);
  const [followUp, setFollowUp] = useState('');
  const [replaces, setReplaces] = useState<KnowledgeEntry | null>(initialReplaces ?? null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const { entries } = usePropertyKnowledge(propertyId);
  const current = useMemo(() => entries.filter((e) => !e.supersededById), [entries]);

  const property = properties.find((p) => p.id === propertyId) ?? null;
  const cars: CarOption[] = property?.cars ?? [];
  const carOption = cars.find((c) => c.id === carId) ?? PROPERTY_WIDE;
  const targetLabel = propertyId
    ? knowledgeTargetLabel(properties, { propertyId, carId: carId || undefined })
    : undefined;
  const suggestion =
    propertyId && !replaces
      ? findSupersedable(current, { propertyId, carId: carId || null, content })
      : undefined;
  const canSave = Boolean(propertyId && kind && content.trim()) && !saving;
  const needsProperty = !propertyId && Boolean(kind || content.trim());

  const onSave = async () => {
    if (!propertyId || !kind || !content.trim() || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    const followUpDate = tasksEnabled && kind === 'commitment' && followUp ? followUp : null;
    try {
      const created = await saveKnowledge(propertyId, {
        ...(carId ? { carId } : {}),
        kind,
        content: content.trim(),
        source: 'manual',
        ...(replaces ? { supersedesId: replaces.id } : {}),
        ...(followUpDate ? { followUpDate } : {}),
      });
      const extras = [
        replaces ? 'replaces an older entry' : '',
        created.followUpTask ? 'follow-up task created' : '',
      ].filter(Boolean);
      enqueueSnackbar([`Saved to ${targetLabel ?? 'the property'}`, ...extras].join(' · '), {
        variant: 'success',
      });
      onClose();
    } catch {
      setError('This could not be saved. Try again.');
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="add-knowledge-title">
      <DialogTitle id="add-knowledge-title">Add knowledge</DialogTitle>
      <DialogContent>
        <Stack spacing="14px" sx={{ pt: 1 }}>
          <Autocomplete
            options={properties}
            value={property}
            disabled={lockCar}
            getOptionLabel={(p) => p.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, value) => {
              setPropertyId(value?.id ?? null);
              setCarId('');
              setReplaces(null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Property"
                size="small"
                required
                error={needsProperty}
                helperText={needsProperty ? 'Pick a property to save this entry.' : undefined}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- opened without a target, picking one is the first step
                autoFocus={!initialTarget}
              />
            )}
          />

          <Autocomplete
            options={[PROPERTY_WIDE, ...cars]}
            value={carOption}
            disableClearable
            disabled={!property || lockCar}
            getOptionLabel={(c) => c.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, value) => {
              if (value.id !== carId) setReplaces(null);
              setCarId(value.id);
            }}
            renderInput={(params) => <TextField {...params} label="Elevator" size="small" />}
          />

          <PillRadioGroup
            label="Type"
            required
            options={KNOWLEDGE_KIND_OPTIONS}
            value={kind}
            onChange={setKind}
            missingHint={!kind && content.trim() ? 'Pick a type to save this entry.' : null}
          />

          <TextField
            label="What should the team know?"
            size="small"
            multiline
            minRows={3}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            slotProps={{ htmlInput: { maxLength: propertiesKnowledgeCreateBodyContentMax } }}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the dialog exists to take this text
            autoFocus={Boolean(initialTarget)}
          />

          {tasksEnabled && kind === 'commitment' ? (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '8px',
                fontSize: 12,
                color: 'text.secondary',
              }}
            >
              <EventOutlinedIcon sx={{ fontSize: 16, color: '#8a5a00' }} />
              <TextField
                type="date"
                size="small"
                label="Follow-up date"
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <span>
                {followUp
                  ? 'Also creates a follow-up task'
                  : 'Optional · add a date to also create a follow-up task'}
              </span>
            </Box>
          ) : null}

          <Autocomplete
            options={current}
            value={replaces}
            disabled={!propertyId}
            getOptionLabel={(e) => entrySummary(e)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, value) => setReplaces(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supersedes… (optional)"
                size="small"
                helperText="The superseded entry stays in history, but the Lift AI Assistant stops using it."
              />
            )}
          />

          {suggestion ? (
            <Box
              data-testid="replace-suggestion"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: '6px 10px',
                borderRadius: 1,
                bgcolor: '#fff7e8',
                border: '1px solid #f0cf8f',
                fontSize: 12,
                color: '#5c4a2a',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>Suggested: “{entrySummary(suggestion, 60)}”</Box>
              <Button size="small" onClick={() => setReplaces(suggestion)}>
                Supersede it
              </Button>
            </Box>
          ) : null}

          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} sx={BUTTON_SX}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void onSave()}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={BUTTON_SX}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
